/**
 * An agent's connector tool waiting, inside a running turn, for the person
 * who SENT the message to connect their own account (item 65A; the Drive
 * pattern, TurnGoogleAccess).
 *
 * The backend publishes `connector_access_needed` and, when the wait ends,
 * `connector_access_resolved` (connector_library.services.connector_access);
 * the turn itself carries on once they connect — the card only asks. "Not
 * now" ends the wait (the model then says it needs their account); Stop ends
 * the turn as always. Never another person's account: the card connects the
 * one signed in here.
 */

import { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import { showNotification } from '@/utils/showNotification';
import { errorBody } from '../services/connectorLibraryService';
import { myConnectorsService, type MyConnector } from '../services/myConnectorsService';
import { ConnectWaiting, KeyForm, useAccountConnect } from './PersonalConnect';
import { ServiceIcon } from './LibraryBits';
import './PersonalConnect.css';

export interface ConnectorAsk {
  entry_id: string;
  name: string;
  kind: 'mcp' | 'api';
  signin_mode: 'per_person_oauth' | 'per_person_key';
  reason: 'not_connected' | 'expired';
  url: string | null;
}

type StreamEvent = { type?: string; entry_id?: string } & Partial<ConnectorAsk>;

export interface TurnConnectorAccess {
  pending: ConnectorAsk | null;
  /** Feed every stream event; true when it was one of the two connector events. */
  onStreamEvent: (event: StreamEvent | null | undefined) => boolean;
  dismiss: () => void;
}

/** `active`: a turn is streaming — the card never outlives it. */
export function useTurnConnectorAccess(active: boolean): TurnConnectorAccess {
  const [pending, setPending] = useState<ConnectorAsk | null>(null);
  useEffect(() => { if (!active) setPending(null); }, [active]);

  const onStreamEvent = useCallback((event: StreamEvent | null | undefined) => {
    if (event?.type === 'connector_access_needed' && event.entry_id) {
      setPending({
        entry_id: event.entry_id, name: event.name ?? '', kind: event.kind === 'api' ? 'api' : 'mcp',
        signin_mode: event.signin_mode === 'per_person_key' ? 'per_person_key' : 'per_person_oauth',
        reason: event.reason === 'expired' ? 'expired' : 'not_connected', url: event.url ?? null,
      });
      return true;
    }
    if (event?.type === 'connector_access_resolved') {
      setPending(prev => (prev && prev.entry_id === event.entry_id ? null : prev));
      return true;
    }
    return false;
  }, []);

  const dismiss = useCallback(() => setPending(null), []);
  return { pending, onStreamEvent, dismiss };
}

export function TurnConnectorAccessCard({ ask, onDismiss }: { ask: ConnectorAsk; onDismiss: () => void }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [keyOpen, setKeyOpen] = useState(false);
  const slug = ask.name.toLowerCase().replace(/[^a-z0-9]+/g, '_');

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch (err) {
      showNotification(errorBody(err).error || t('myConnectors.error'), 'error');
    } finally { setBusy(false); }
  };

  // Connected: the waiting call sees it and carries on; the resolved event closes the card.
  const account = useAccountConnect(ask.name);
  const connect = () => {
    if (ask.signin_mode === 'per_person_key') { setKeyOpen(true); return; }
    account.start(ask.entry_id);
  };

  const notNow = () => run(async () => {
    await myConnectorsService.notNow(ask.entry_id);
    onDismiss();
  });

  const asKey: MyConnector = {
    entry_id: ask.entry_id, name: ask.name, kind: ask.kind, slug, description: '', signin_mode: ask.signin_mode,
    url: ask.url, status: 'not_connected', account_label: null, paused_reason: null, removed_reason: null,
    connected_at: null,
  };

  return (
    <div className="pcx pcx-ask" role="alert" data-testid="turn-connector-access">
      <ServiceIcon name={ask.name} slug={slug} url={ask.url} />
      <div className="pcx__text">
        <span className="pcx__name">
          {t(ask.reason === 'expired' ? 'myConnectors.ask.titleExpired' : 'myConnectors.ask.title', { name: ask.name })}
        </span>
        <span className="pcx__status">{t('myConnectors.ask.body', { name: ask.name })}</span>
      </div>
      {!keyOpen && account.waiting && (
        <div className="pcx__actions">
          <ConnectWaiting onCancel={account.cancel} testId="turn-connector-access-waiting" />
        </div>
      )}
      {!keyOpen && !account.waiting && (
        <div className="pcx__actions">
          <button type="button" className="cx-btn cx-btn--sm" disabled={busy} onClick={() => { void notNow(); }}
                  data-testid="turn-connector-access-not-now">{t('myConnectors.ask.notNow')}</button>
          <button type="button" className="cx-btn cx-btn--primary cx-btn--sm" disabled={busy} onClick={connect}
                  data-testid="turn-connector-access-connect">{t('myConnectors.connect')}</button>
        </div>
      )}
      {keyOpen && (
        <KeyForm connector={asKey} busy={busy} onCancel={() => setKeyOpen(false)}
                 onSave={credential => { void run(async () => { await myConnectorsService.saveKey(ask.entry_id, credential); }); }} />
      )}
    </div>
  );
}
